import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import {
    AspectRatio,
    type GallerySettings,
    type ProductCardWidgetData,
    RecurringMode,
    ViewModalType,
} from '@web/widgets/product-card/type';
import type { Classes } from 'jss';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';
import { EButtonStyleType } from '@common/enums';
import { type SelectedFileExtended } from '@common/types/file.type';
import { SettingsFacade } from '@web/store/website/settings/settings.facade';
import { SpSafeHtmlPipe } from '@common/pipes/safe-html.pipe';
import { CommonModule } from '@angular/common';
import type { Observable } from 'rxjs';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { environment } from '@web-env/environment';
import { TranslateModule } from '@ngx-translate/core';
import { formatFileUrl, formatFileUrlWithResize, resolveCdnBaseUrl } from '@components/sp-file-manager/src/helpers';
import { SppFallbackImgDirective } from '@common/directives/fallback-img.directive';

@Component({
    selector: 'web-modal-view-card',
    templateUrl: './modal-view-card.component.html',
    styleUrls: ['./modal-view-card.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, SpSafeHtmlPipe, TranslateModule, SppFallbackImgDirective],
})
export class ModalViewCardComponent {
    protected readonly settingsFacade = inject(SettingsFacade);
    protected readonly layoutFacade = inject(LayoutFacade);

    @Input() data: ProductCardWidgetData;
    @Input() classes: Classes;

    public aspectRatio = AspectRatio;

    public readonly shopSettings$ = this.settingsFacade.shopSettings$;
    public readonly isDeviceMobile$: Observable<boolean> = this.layoutFacade.isDeviceMobile$;

    public get cardModalViewType(): ViewModalType {
        return this.data.cardModalViewType;
    }

    public get gallerySettings(): GallerySettings {
        return this.data.previewModalSettings.gallery as GallerySettings;
    }

    public get horizontalViewType(): boolean {
        return this.cardModalViewType === ViewModalType.horizontal;
    }

    public get verticalViewType(): boolean {
        return this.cardModalViewType === ViewModalType.vertical;
    }

    public get element(): ProductCardWidgetData['element'] {
        return this.data.element;
    }

    public get previewModalSettings(): ProductCardWidgetData['previewModalSettings'] {
        return this.data.previewModalSettings;
    }

    public get wrapperClass(): string {
        return this.data.cardModalViewType === ViewModalType.horizontal
            ? this.classes?.previewHorizontalViewWrapper
            : this.classes?.previewVerticalViewWrapper;
    }

    public get showMoreButton(): ProductCardWidgetData['showMoreButton'] {
        return this.data.showMoreButton;
    }

    public get buttonClass(): string {
        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[this.showMoreButton.size]}`;
        const shadowClass = this.classes?.showMoreButtonShadow ?? '';

        if (this.showMoreButton.styleType === EButtonStyleType.custom) {
            return `${styles} ${this.classes?.showMoreButtonStyle} ${shadowClass}`;
        }

        return `${styles} ${BUTTON_TYPE_CLASS_MAP[this.showMoreButton.styleType]} ${shadowClass}`;
    }

    public formatImageURL(image: SelectedFileExtended): string {
        return formatFileUrlWithResize(image, this.getImageBaseUrl(image), 720);
    }

    public formatImageFallbackURL(image: SelectedFileExtended): string {
        return formatFileUrl(image, this.getImageBaseUrl(image));
    }

    private getImageBaseUrl(image: SelectedFileExtended): string {
        return resolveCdnBaseUrl(image?.defaultCdnUrl, environment.fileManagerCdnUrl);
    }

    public get subscriptionPeriodKey(): string | null {
        if (!this.isSubscriptionType) {
            return;
        }

        switch (this.element?.subscriptionPeriod) {
            case RecurringMode.daily:
                return 'widget_product_subscription_period_day';
            case RecurringMode.weekly:
                return 'widget_product_subscription_period_week';
            case RecurringMode.monthly:
                return 'widget_product_subscription_period_month';
            case RecurringMode.quarterly:
                return 'widget_product_subscription_period_3_months';
            case RecurringMode.halfyearly:
                return 'widget_product_subscription_period_6_months';
            case RecurringMode.yearly:
                return 'widget_product_subscription_period_year';
            default:
                return null;
        }
    }

    public get isSubscriptionType(): boolean {
        return Boolean(this.element?.subscriptionPeriod);
    }

    public get isShowCatalogCard(): boolean {
        if (!this.element?.catalogProductId) return true;

        const isVisible = this.element?.visible ?? 1;
        return !!this.element?.catalogProductId && !!isVisible;
    }
}
